import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import UserLayout from '@/components/Layout/UserLayout.tsx';
import { showSpinner, hideSpinner } from '@/components/Layout/LoadingSpinner';
import Alert from '@/components/UI/Alert.tsx';
import Card from '@/components/UI/Card.tsx';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import { trpc } from '@/api/trpc.ts';
import { useAppQuery } from '@/hooks/useAppQuery';
import TradeLogicExecutionJournalTrace, {
    type TradeLogicExecutionJournalRecord
} from '@/components/TradeLogic/TradeLogicExecutionJournalTrace.tsx';

const buildRealTimeEntriesHref = (params: { strategyId: number | null; entityId: number | null; entityType: string | null }): string => {
    const searchParams = new URLSearchParams();

    if (params.strategyId !== null) {
        searchParams.set('strategyId', params.strategyId.toString());
    }

    if (params.entityId !== null) {
        searchParams.set('entityId', params.entityId.toString());
    }

    if (params.entityType !== null) {
        searchParams.set('entityType', params.entityType);
    }

    const queryString = searchParams.toString();

    if (!queryString) {
        return '/user/realTimeEntries';
    }

    return `/user/realTimeEntries?${queryString}`;
};

const detailLinkClassName =
    'inline-flex items-center rounded-md border border-zinc-700 bg-zinc-950/80 px-3 py-2 text-sm font-medium text-zinc-100 hover:border-zinc-500 hover:text-white';

const buildTradeLogicActivityHref = (journal: TradeLogicExecutionJournalRecord): string => {
    const searchParams = new URLSearchParams();

    searchParams.set('definitionId', journal.row.definitionId.toString());

    if (journal.row.strategyId !== null) {
        searchParams.set('strategyId', journal.row.strategyId.toString());
    }

    if (journal.row.tradeId !== null) {
        searchParams.set('tradeId', journal.row.tradeId.toString());
    }

    if (journal.row.entityId !== null) {
        searchParams.set('entityId', journal.row.entityId.toString());
    }

    if (journal.row.entityType !== null) {
        searchParams.set('entityType', journal.row.entityType);
    }

    return `/user/tradeLogicExecutionJournals?${searchParams.toString()}`;
};

const TradeLogicExecutionJournal: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [error, setError] = useState<string | null>(null);
    const journalId = id ? parseInt(id, 10) : Number.NaN;
    const isValidJournalId = Number.isInteger(journalId) && journalId > 0;

    useEffect(() => {
        showSpinner();

        return () => {
            hideSpinner();
        };
    }, []);

    const journalQuery = useAppQuery(
        {
            ...trpc.strategy.fetchTradeLogicExecutionJournal.queryOptions({
                journalId: isValidJournalId ? journalId : 0
            }),
            enabled: isValidJournalId
        },
        {
            fallbackErrorMessage: 'Failed to load Trade Logic execution journal',
            showErrorToast: true,
            setError: (message) => setError(message)
        }
    );

    const journal: TradeLogicExecutionJournalRecord | null = journalQuery.data?.journal ?? null;

    const relatedLinks = useMemo(() => {
        if (!journal) {
            return [];
        }

        const links: { label: string; to: string }[] = [];

        links.push({
            label: 'Open activity index',
            to: buildTradeLogicActivityHref(journal)
        });

        if (journal.row.strategyId !== null) {
            links.push({
                label: 'Open strategy',
                to: `/user/tradingStrategy/${journal.row.strategyId}`
            });
        }

        if (journal.row.tradeId !== null) {
            links.push({
                label: 'Open trade',
                to: `/user/visualizeTrade/${journal.row.tradeId}`
            });
        }

        if (journal.row.entryLogId !== null) {
            links.push({
                label: 'Open entry log',
                to: `/user/entryLog/?entryLogId=${journal.row.entryLogId}`
            });
        } else if (journal.row.realTimeStrategyEntryId !== null) {
            links.push({
                label: 'Open real-time entries',
                to: buildRealTimeEntriesHref({
                    strategyId: journal.row.strategyId,
                    entityId: journal.row.entityId,
                    entityType: journal.row.entityType
                })
            });
        }

        return links;
    }, [journal]);

    return (
        <UserLayout title="Trade Logic Execution Journal">
            <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
                {!isValidJournalId && <Alert variant="danger">Invalid Trade Logic execution journal id.</Alert>}
                {error && <Alert variant="danger">{error}</Alert>}
                {journalQuery.isLoading && <DataLoadingSpinner text="Loading Trade Logic execution journal..." />}
                {!journalQuery.isLoading && isValidJournalId && !error && !journal && (
                    <Alert variant="warning">Trade Logic execution journal not found or you do not have access to it.</Alert>
                )}

                {journal && (
                    <>
                        <Card>
                            <div className="flex flex-col gap-4">
                                <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                                    <div>
                                        <h2 className="text-xl font-semibold text-white">Trade Logic V2 Journal #{journal.row.id}</h2>
                                        <div className="mt-1 text-sm text-zinc-400">
                                            {journal.row.phase} / {journal.row.status} / {journal.row.actionKind}
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {relatedLinks.map((link) => (
                                            <Link className={detailLinkClassName} key={link.label} to={link.to}>
                                                {link.label}
                                            </Link>
                                        ))}
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                                    <div className="rounded-md border border-zinc-800 bg-zinc-950/80 p-3">
                                        <div className="text-[11px] uppercase tracking-wide text-zinc-500">Definition</div>
                                        <div className="mt-1 font-mono text-sm text-zinc-100">#{journal.row.definitionId}</div>
                                    </div>
                                    <div className="rounded-md border border-zinc-800 bg-zinc-950/80 p-3">
                                        <div className="text-[11px] uppercase tracking-wide text-zinc-500">Compiled version row</div>
                                        <div className="mt-1 font-mono text-sm text-zinc-100">#{journal.row.definitionVersionId}</div>
                                    </div>
                                    <div className="rounded-md border border-zinc-800 bg-zinc-950/80 p-3">
                                        <div className="text-[11px] uppercase tracking-wide text-zinc-500">Scope</div>
                                        <div className="mt-1 break-all font-mono text-sm text-zinc-100">{journal.row.scopeKey}</div>
                                    </div>
                                    <div className="rounded-md border border-zinc-800 bg-zinc-950/80 p-3">
                                        <div className="text-[11px] uppercase tracking-wide text-zinc-500">Created at</div>
                                        <div className="mt-1 text-sm text-zinc-100">{journal.row.createdAt}</div>
                                    </div>
                                    <div className="rounded-md border border-zinc-800 bg-zinc-950/80 p-3">
                                        <div className="text-[11px] uppercase tracking-wide text-zinc-500">Candidate</div>
                                        <div className="mt-1 break-all font-mono text-sm text-zinc-100">
                                            {journal.row.candidateId ?? 'N/A'}
                                        </div>
                                    </div>
                                    <div className="rounded-md border border-zinc-800 bg-zinc-950/80 p-3">
                                        <div className="text-[11px] uppercase tracking-wide text-zinc-500">Conflict key</div>
                                        <div className="mt-1 break-all font-mono text-sm text-zinc-100">
                                            {journal.row.conflictKey ?? 'N/A'}
                                        </div>
                                    </div>
                                    <div className="rounded-md border border-zinc-800 bg-zinc-950/80 p-3">
                                        <div className="text-[11px] uppercase tracking-wide text-zinc-500">Observation</div>
                                        <div className="mt-1 break-all font-mono text-sm text-zinc-100">
                                            {journal.row.observationId ?? 'N/A'}
                                        </div>
                                    </div>
                                    <div className="rounded-md border border-zinc-800 bg-zinc-950/80 p-3">
                                        <div className="text-[11px] uppercase tracking-wide text-zinc-500">Entity</div>
                                        <div className="mt-1 text-sm text-zinc-100">
                                            {journal.row.entityType && journal.row.entityId !== null
                                                ? `${journal.row.entityType} #${journal.row.entityId}`
                                                : 'N/A'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>

                        <Card>
                            <div className="mb-4">
                                <h3 className="text-lg font-semibold text-white">Execution Trace</h3>
                                <div className="mt-1 text-sm text-zinc-400">
                                    This page shows the persisted journal row plus the parsed Trade Logic payload written at execution time.
                                </div>
                            </div>
                            <TradeLogicExecutionJournalTrace journal={journal} />
                        </Card>
                    </>
                )}
            </div>
        </UserLayout>
    );
};

export default TradeLogicExecutionJournal;
